import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { STORAGE_DETAILS } from "../lib/constants";
import WorkflowProgressBar from "../components/WorkflowProgressBar";
import { ChevronLeft, Warehouse, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Step 2 of the Yard Runner workflow: STOCK.
 * Forces the runner to choose a Storage_Detail before continuing.
 */
export default function StockShipment() {
  const { id } = useParams();
  const nav = useNavigate();
  const [shipment, setShipment] = useState(null);
  const [storage, setStorage] = useState("");
  const [storageOther, setStorageOther] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/shipments/${id}`);
        setShipment(data);
        setStorage(data.Storage_Detail || "");
        setStorageOther(data.Storage_Other || "");
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || "Not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const onConfirm = async () => {
    setError("");
    if (!storage) {
      setError("Pick a bay/rack/shelf — required to stock.");
      return;
    }
    if (storage === "Other Storage" && !storageOther.trim()) {
      setError("Describe the 'Other' storage location.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/shipments/${id}/stock`, {
        Storage_Detail: storage,
        Storage_Other: storageOther,
      });
      toast.success(`Stocked at ${storage}`);
      nav(`/shipments/${id}`, { replace: true });
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Stock failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="max-w-3xl mx-auto p-6 text-slate-500">Loading…</div>;
  }
  if (!shipment) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="rg-card p-6 text-rose-700 font-bold">{error || "Not found"}</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-32 md:pb-10">
      <button
        onClick={() => nav(-1)}
        data-testid="back-btn"
        className="flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-slate-600 hover:text-slate-900 mb-3"
      >
        <ChevronLeft className="h-4 w-4" /> Back
      </button>

      <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-700">
        <Warehouse className="h-4 w-4" />
        Step 2 of 4 — Stock in Yard
      </div>
      <h1 className="font-display text-3xl uppercase text-slate-900 mb-1">
        Stock <span className="font-mono">{shipment.MR_Number}</span>
      </h1>
      <p className="text-sm text-slate-600 mb-5">
        Tell the Yard Runner where the material is being placed. Required before destock.
      </p>

      <div className="rg-card p-4 sm:p-5 mb-5">
        <WorkflowProgressBar stage="Received" testid="stock-progress" />
      </div>

      <div className="rg-card p-5 mb-5 border-2 border-amber-400 bg-amber-50">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700 mb-2">
          Yard storage — where exactly is it being placed?
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="stock-storage-grid">
          {STORAGE_DETAILS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStorage(s)}
              data-testid={`stock-storage-${s.replace(/\s+/g, "-")}`}
              className={`rg-tap rounded-sm border-2 px-3 py-3 text-sm font-bold uppercase tracking-wide flex items-center justify-center text-center min-h-[60px] ${
                storage === s
                  ? "bg-slate-900 text-white border-amber-500"
                  : "bg-white border-slate-300 text-slate-800 hover:border-slate-900"
              }`}
            >
              {storage === s && <CheckCircle2 strokeWidth={3} className="h-4 w-4 mr-1.5 text-amber-400" />}
              {s}
            </button>
          ))}
        </div>
        {storage === "Other Storage" && (
          <input
            value={storageOther}
            onChange={(e) => setStorageOther(e.target.value)}
            data-testid="stock-storage-other"
            placeholder="Describe storage location"
            className="rg-tap mt-3 w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
          />
        )}
      </div>

      {error && (
        <div className="border-2 border-rose-600 bg-rose-50 text-rose-800 text-sm font-bold px-3 py-2 rounded-sm mb-4" data-testid="form-error">
          {error}
        </div>
      )}

      <div className="md:hidden h-20" />
      <div className="fixed md:static bottom-16 md:bottom-auto left-0 right-0 md:left-auto md:right-auto z-30 bg-white md:bg-transparent border-t-2 md:border-t-0 border-slate-300 p-4 md:p-0 space-y-2">
        <button
          onClick={onConfirm}
          disabled={submitting || !storage}
          data-testid="confirm-stock-btn"
          className="rg-tap-xl w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 disabled:opacity-50 min-h-[64px] text-base sm:text-lg border-2 border-amber-700"
        >
          <CheckCircle2 strokeWidth={3} className="h-6 w-6" />
          {submitting ? "Stocking…" : "Confirm Stocked in Yard"}
          <ArrowRight strokeWidth={3} className="h-5 w-5" />
        </button>
        <Link
          to={`/shipments/${id}`}
          data-testid="skip-to-detail-link"
          className="block text-center text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900 underline"
        >
          Skip — go to detail page
        </Link>
      </div>
    </div>
  );
}
